import { useState, useEffect } from 'react';

import AccordionBasic from "@/components/accordions/AccordionBasic";
import UpdateDayPartsSection from "@/components/settings/locations/sections/UpdateDayPartsSection";
import { DayPart } from "@/types/DayPart";
import { useData } from '@/hooks/useData';
import StandardButton from '@/components/buttons/StandardButton';
import { useToast } from '@/hooks/useToast';
import { updateFranchiseConfiguration } from '@/utils/api';
import { FranchiseConfiguration } from '@/types/FranchiseConfiguration';

export default function UpdateFranchiseDayPartsConfiguration(): JSX.Element {
  const {
      selectedFranchise,
      franchiseConfiguration,
      setFranchiseConfiguration,
      toggled,
  } = useData();
  const { setToast } = useToast();
  const generateId = () => Math.random().toString(36).substr(2, 9);
  const [newDayParts, setNewDayParts] = useState<DayPart[]>(
    (toggled === 'DT' ? selectedFranchise?.configuration.configuration.driveThruConfiguration.dayParts ?? [] : selectedFranchise?.configuration.configuration.lobbyConfiguration.dayParts ?? []).map((dayPart) => ({
      ...dayPart,
      id: dayPart.id || generateId(), // Ensure an id exists
    }))
  );
  const [editFranchiseConfiguration, setEditFranchiseConfiguration] = useState<FranchiseConfiguration | null>();
  

  useEffect(() => {
      setEditFranchiseConfiguration(franchiseConfiguration);
  }, [franchiseConfiguration]);

  useEffect(() => {
    if (!editFranchiseConfiguration) return;

    const editDayParts = newDayParts.map(dayPart => {
      delete dayPart.id
      return dayPart;
    });

    if (toggled === 'DT') {
      setEditFranchiseConfiguration({
        ...editFranchiseConfiguration,
        driveThruConfiguration: {
          ...editFranchiseConfiguration?.driveThruConfiguration,
          dayParts: editDayParts
        }
      });
    } else {
      setEditFranchiseConfiguration({
        ...editFranchiseConfiguration,
        lobbyConfiguration: {
          ...editFranchiseConfiguration?.lobbyConfiguration,
          dayParts: editDayParts
        }
      });
    }
  }, [newDayParts])

  // Handle updating the new day parts.
  function handleDayPartsInputChange(
    e: React.ChangeEvent<HTMLInputElement>
  ): void {
    const { name, value } = e.target;
    const id = name.split('-')[0]; // Extract ID

    setNewDayParts((prevDayParts) => {
      return prevDayParts.map((dayPart) =>
        dayPart.id === id && dayPart.name !== value
          ? { ...dayPart, name: value }
          : dayPart
      );
    });
  }

  // Handle updating the time for a day part.
  function handleUpdateDayPartTime(
    name: string,
    dateStr: string,
    isFrom: boolean
  ): void {
    const dayPartIndex = newDayParts.findIndex(
      (dayPart) => dayPart.name === name
    );
    const newDayPartsCopy: any[] = [...newDayParts];
    if (isFrom) {
      newDayPartsCopy[dayPartIndex].from = dateStr;
    } else {
      newDayPartsCopy[dayPartIndex].to = dateStr;
    }
    setNewDayParts(newDayPartsCopy);
  }

  // Handle adding a new day part.
  function handleAddDayPart(): void {
    setNewDayParts([
      ...newDayParts,
      {
        name: 'New Day Part',
        from: '',
        to: '',
        targetTime: 0,
        percentage: 0,
        id: generateId(),
      },
    ]);
  }
    
  // Handle deleting a day part.
  function handleDeleteDayPart(name: string): void {
    setNewDayParts(newDayParts.filter((dayPart) => dayPart.name !== name));
  }

  async function handleSaveDayParts(): Promise<void> {
    if (!editFranchiseConfiguration) return;

    updateFranchiseConfiguration(selectedFranchise!.id, editFranchiseConfiguration)
    .then(() => {
      if (toggled === 'DT') {
        setFranchiseConfiguration({
          ...editFranchiseConfiguration,
          driveThruConfiguration: {
            ...editFranchiseConfiguration?.driveThruConfiguration,
            dayParts: editFranchiseConfiguration?.driveThruConfiguration.dayParts
          }
        });
      } else {
        setFranchiseConfiguration({
          ...editFranchiseConfiguration,
          lobbyConfiguration: {
            ...editFranchiseConfiguration?.lobbyConfiguration,
            dayParts: editFranchiseConfiguration?.lobbyConfiguration.dayParts
          }
        });
      }
      setToast({
        show: true,
        message: `Franchise day parts updated for ${toggled === 'DT' ? 'Drive Thru' : 'Lobby'}`,
        type: 'success',
      });
    })
    .catch((error) => {
        setToast({
          show: true,
          message: `Error updating franchise day parts ${toggled === 'DT' ? 'Drive Thru' : 'Lobby'}`,
          type: 'error',
        });
        console.error(
        `Error updating Franchise configuration for ${selectedFranchise?.id}:`,
        error
        );
      });
    }
  
  function handleSave(): void {
    handleSaveDayParts().catch((error) => {
      console.error('Error saving franchise day part settings', error);
      setToast({
        show: true,
        message: 'Error saving franchise day part settings',
        type: 'error',
      });
    });
  }

  return (
  <AccordionBasic title="Franchise Day Parts Configuration" dataCy="franchise-regions-accordion">
    {newDayParts && (
      <UpdateDayPartsSection 
        dayParts={newDayParts}
        handleInputChange={handleDayPartsInputChange}
        handleTimeChange={handleUpdateDayPartTime}
        handleAdd={handleAddDayPart}
        handleDelete={handleDeleteDayPart}
      />
    )}
    {/* <UpdateDayPartsSection 
    dayParts={newDayParts}
    handleInputChange={handleDayPartsInputChange}
    handleTimeChange={handleUpdateDayPartTime}
    handleAdd={handleAddDayPart}
    handleDelete={handleDeleteDayPart}
    /> */}
    <div className="flex flex-wrap gap-8 w-full">
      <StandardButton
      onClick={handleSave}
      dataCy="update-day-part-button"
      >
        Save Updates
      </StandardButton>
    </div>
  </AccordionBasic>
  )
}